import { useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import {
	COLORS,
	MaterialIcons,
	notificationService,
	TextButton,
} from '@monthio/react-components'
import { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const DropZone = styled.div`
	background: ${COLORS.SECONDARY};
	color: ${COLORS.PRIMARY};
	border-radius: 4px;
	border: 1px dashed ${COLORS.PRIMARY};
	text-align: center;
	font-weight: 600;
`

const StyledDiv = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	padding: 8px 24px;
`

const StyledSpan = styled.span`
	position: sticky;
	left: 50%;
	transform: translateX(-50%);
	font-size: 12px;
	cursor: pointer;
`

const List = styled.ul`
	max-height: 11vh;
	overflow: scroll;
	list-style: none;
	margin: 16px 0 0;
	padding: 0;

	& > li {
		display: flex;
		align-items: center;
		padding: 4px 20px 2px 0;
	}
`
const Wrapper = styled.div`
	margin-top: 20px;
`

const FileName = styled.span`
	margin-left: 6px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	${(props) => props.theme.breakpoints.down('xs')} {
		font-size: 12px;
	}
`

export const DocumentsUploader = ({
	handleChange,
	setFieldValue,
	setFieldTouched,
	uploadedFiles,
}: {
	handleChange?: (files: File[]) => void
	setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void
	setFieldTouched?: (
		field: string,
		isTouched?: boolean,
		shouldValidate?: boolean,
	) => void
	uploadedFiles?: File[]
}): JSX.Element => {
	const theme = useTheme()
	const { t } = useTranslation()

	const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
	const [files, setFiles] = useState(uploadedFiles ?? [])

	const onDrop = useCallback((acceptedFiles) => {
		setFiles((prevState) => [...prevState, ...acceptedFiles])
	}, [])

	const { getRootProps, getInputProps, fileRejections } = useDropzone({
		onDrop,
		accept: {
			'image/jpeg': ['.jpeg', '.jpg'],
			'image/png': ['.png'],
			'image/bmp': ['.bmp'],
			'image/webp': ['.webp'],
			'application/pdf': ['.pdf'],
		},
		maxSize: 10000000,
		minSize: 1,
	})

	useEffect(() => {
		setFieldValue?.('uploadedFiles', files)
		handleChange?.(files)
	}, [files, handleChange, setFieldValue])

	useEffect(() => {
		fileRejections?.map(({ errors }) => {
			errors?.map((error) => {
				notificationService.error(error.message)
			})
		})
	}, [fileRejections])

	const filesList = files.map((file, index) => (
		<li key={file.name + index}>
			<TextButton onClick={() => removeFile(index)}>
				<MaterialIcons.DeleteOutline
					htmlColor={COLORS.OTHER_COLORS.ERROR_RED_LIGHT}
				/>
			</TextButton>
			<FileName>{file.name}</FileName>
		</li>
	))

	const removeFile = (index: number) => {
		const array = [...files]
		array.splice(index, 1)
		setFiles(array)
	}

	return (
		<Wrapper>
			<DropZone
				{...getRootProps()}
				onBlur={() => {
					setFieldTouched?.('uploadedFiles', true)
				}}
			>
				<input {...getInputProps()} />
				<StyledDiv>
					<MaterialIcons.CloudUpload />
					{isSmallScreen ? (
						<StyledSpan>
							{t('miniBudgetFormV2-documentsUploader-mobile-text')}
						</StyledSpan>
					) : (
						<StyledSpan>
							{t('miniBudgetFormV2-documentsUploader-desktop-text-part1')}
							<br />
							<u>
								{t('miniBudgetFormV2-documentsUploader-desktop-text-part2')}
							</u>
						</StyledSpan>
					)}
				</StyledDiv>
			</DropZone>
			{filesList.length > 0 ? <List>{filesList}</List> : null}
		</Wrapper>
	)
}
